import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../../components/admin/AdminNav';
import { generateCSV, downloadCSV } from '../../utils/csvHelper';
import EditSubmissionModal from '../../components/EditSubmissionModal';
import AnalyticsMap from '../../components/admin/AnalyticsMap';
import DuplicateListModal from '../../components/admin/DuplicateListModal';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Region mapping based on state codes (same as ApprovalWorkflow.jsx)
const STATE_TO_REGION = {
  // Southwest
  'TX': 'Southwest', 'OK': 'Southwest', 'AR': 'Southwest', 'LA': 'Southwest',
  // Southeast  
  'GA': 'Southeast', 'FL': 'Southeast', 'AL': 'Southeast', 'MS': 'Southeast',
  'TN': 'Southeast', 'SC': 'Southeast', 'NC': 'Southeast',
  // Mid-Atlantic
  'PA': 'Mid-Atlantic', 'NY': 'Mid-Atlantic', 'NJ': 'Mid-Atlantic',
  'MD': 'Mid-Atlantic', 'VA': 'Mid-Atlantic', 'WV': 'Mid-Atlantic', 'DE': 'Mid-Atlantic', 'DC': 'Mid-Atlantic',
  // Midwest
  'OH': 'Midwest', 'IN': 'Midwest', 'IL': 'Midwest', 'MI': 'Midwest',
  'WI': 'Midwest', 'MN': 'Midwest', 'IA': 'Midwest', 'MO': 'Midwest',
  'KS': 'Midwest', 'NE': 'Midwest', 'SD': 'Midwest', 'ND': 'Midwest',
  // Northeast
  'MA': 'Northeast', 'CT': 'Northeast', 'RI': 'Northeast',
  'VT': 'Northeast', 'NH': 'Northeast', 'ME': 'Northeast',
  // West
  'CA': 'West', 'NV': 'West', 'AZ': 'West', 'UT': 'West',
  'CO': 'West', 'NM': 'West', 'WA': 'West', 'OR': 'West', 'ID': 'West',
  'MT': 'West', 'WY': 'West', 'AK': 'West', 'HI': 'West',
  // South (remaining southern states)
  'KY': 'South', 'PR': 'South'
};

// All regions for the bar chart (in competitive order)
const ALL_REGIONS = ['Southeast', 'Southwest', 'Midwest', 'Mid-Atlantic', 'Northeast', 'West', 'South'];

// Region colors for competitive visual appeal
const REGION_COLORS = {
  'Southeast': '#0DB14B',    // PBFS Green - Primary
  'Southwest': '#235EAC',    // PBFS Blue
  'Midwest': '#FFC107',      // Gold/Yellow
  'Mid-Atlantic': '#00314F', // PBFS Navy
  'Northeast': '#17A2B8',    // Teal
  'West': '#DC3545',         // Red
  'South': '#6F42C1'         // Purple
};

const AnalyticsReports = ({ backendUrl, onViewUserSubmissions }) => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('all'); // 7d, 30d, 90d, all - Default to All Time
  const [exportFormat, setExportFormat] = useState('csv'); // csv, json
  const [exportAllData, setExportAllData] = useState(false); // Export all dates checkbox
  
  // 🆕 Actual date range state for the date picker inputs
  const [exportStartDate, setExportStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  const [exportEndDate, setExportEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [topSubmitters, setTopSubmitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchKeyRef = useRef(''); // prevent duplicate fetches (React 18 strict-mode double-invocation)
  const [stats, setStats] = useState({
    totalInspections: 0,
    approvalRate: 0,
    avgResponseTime: '0 hours',
    topUser: ''
  });

  const [allInspections, setAllInspections] = useState([]);

  // Chart drill-down modal state
  const [drillDownModal, setDrillDownModal] = useState({
    isOpen: false,
    title: '',
    type: '', // 'region', 'status', 'timeline'
    data: null,
    filterValue: null
  });

  // 🔧 Status filter within drill-down modal (for region view)
  const [drillDownStatusFilter, setDrillDownStatusFilter] = useState(null); // 'approved' | 'pending' | 'rejected' | null

  // Edit submission modal state
  const [editingInspection, setEditingInspection] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Duplicate list modal state
  const [showDuplicateListModal, setShowDuplicateListModal] = useState(false);

  // Track AdminNav mode
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Listen for AdminNav toggle events
  useEffect(() => {
    const handleNavToggle = (e) => {
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  // Fetch real user data and inspections from backend
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        const key = `${backendUrl || ''}|${dateRange || ''}`;
        if (fetchKeyRef.current === key) {
          // Avoid double-fetching in React dev strict mode (and reduce Cosmos RU pressure).
          return;
        }
        fetchKeyRef.current = key;

        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

        const fetchWithCosmosBackoff = async (url, { maxAttempts = 3 } = {}) => {
          let lastErr = null;
          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(url, { 
                headers: { 
                  'X-Local-Dev': 'true',
                  ...(token && { 'Authorization': `Bearer ${token}` })
                } 
              });
              const text = await res.text();
              if (res.ok) return { ok: true, status: res.status, text };

              // Cosmos throttling often bubbles as 500 with RetryAfterMs in the error string.
              const retryMatch = text.match(/RetryAfterMs=(\d+)/i);
              const retryMs = retryMatch ? Number(retryMatch[1]) : null;
              const isThrottled = res.status === 429 || (text || '').toLowerCase().includes('toomanyrequests') || (text || '').includes('16500');
              if (isThrottled && attempt < maxAttempts) {
                await sleep(Math.max(200, Math.min(3000, retryMs || 600)) * attempt);
                continue;
              }
              return { ok: false, status: res.status, text };
            } catch (e) {
              lastErr = e;
              // Network or server busy; small backoff then retry.
              if (attempt < maxAttempts) {
                await sleep(400 * attempt);
                continue;
              }
              throw lastErr;
            }
          }
          throw lastErr || new Error('Fetch failed');
        };

        // Fetch user stats
        console.log('📊 Fetching user stats from:', `${backendUrl}/api/admin/users/stats`);
        const usersResponse = await fetchWithCosmosBackoff(`${backendUrl}/api/admin/users/stats`, { maxAttempts: 3 });

        // Fetch inspections for analytics calculations
        // SECURITY FIX: Use admin endpoint (has full data including submitted_by)
        // Admin pages should NOT use public endpoints
        const token = localStorage.getItem('token');
        const inspectionsResponse = await fetchWithCosmosBackoff(
          `${backendUrl}/api/admin/inspections?limit=10000&status=all`, 
          { 
            maxAttempts: 3,
            headers: {
              ...(token && { 'Authorization': `Bearer ${token}` })
            }
          }
        );

        const response = usersResponse;

        console.log('📊 Response status:', response.status);

        if (response.ok) {
          const responseText = response.text;

          let data;
          try {
            data = JSON.parse(responseText);
          } catch (parseError) {
            console.error('❌ JSON parse error:', parseError);
            setLoading(false);
            return;
          }

          if (!data.users || data.users.length === 0) {
            console.log('⚠️ No users in analytics data');
            setLoading(false);
            return;
          }

          if (inspectionsResponse.ok) {
            const inspectionsText = inspectionsResponse.text;
            try {
              const inspectionsData = JSON.parse(inspectionsText);
              if (inspectionsData.inspections) {
                const allInspectionsData = inspectionsData.inspections;

                // Store ALL inspections for charts
                setAllInspections(allInspectionsData);

                // Filter by date range
                const now = new Date();
                const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : null;
                let filteredInspections = allInspectionsData;

                if (days) {
                  const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
                  filteredInspections = allInspectionsData.filter(insp => {
                    const date = new Date(insp.created_at || insp.capture_date);
                    return date >= cutoffDate;
                  });
                }

                const totalInspectionsCount = filteredInspections.length;

                // Calculate approval rate from filtered data
                const approvedCount = filteredInspections.filter(i => i.approval_status === 'approved' || i.status === 'approved').length;
                const approvalRate = totalInspectionsCount > 0 ? ((approvedCount / totalInspectionsCount) * 100).toFixed(1) : 0;

                // Calculate average response time from filtered data
                let avgResponseTime = '0 hours';
                const responseTimes = [];
                filteredInspections.forEach(inspection => {
                  if ((inspection.approval_status === 'approved' || inspection.approval_status === 'rejected') &&
                    inspection.submitted_at && inspection.approved_at) {
                    const submitTime = new Date(inspection.submitted_at);
                    const approveTime = new Date(inspection.approved_at);
                    const diffHours = (approveTime - submitTime) / (1000 * 60 * 60);
                    if (diffHours >= 0) responseTimes.push(diffHours);
                  }
                });

                if (responseTimes.length > 0) {
                  const avgHours = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
                  avgResponseTime = avgHours < 1 ?
                    `${Math.round(avgHours * 60)} min` :
                    `${avgHours.toFixed(1)} hours`;
                }

                // Calculate top submitters from filtered data
                const userCounts = {};
                filteredInspections.forEach(insp => {
                  const name = insp.submitted_by || 'Unknown';
                  const email = insp.submitted_by_email || '';
                  const key = email || name;

                  if (!userCounts[key]) {
                    userCounts[key] = { name, email, count: 0 };
                  }
                  userCounts[key].count++;
                });

                const sortedUsers = Object.values(userCounts)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 5)
                  .map((user, index) => ({
                    rank: index + 1,
                    name: user.name || user.email.split('@')[0],
                    email: user.email,
                    count: user.count,
                    points: user.count
                  }));

                console.log('📊 Approval rate:', approvalRate, '% Response time:', avgResponseTime);

                setTopSubmitters(sortedUsers);
                setStats({
                  totalInspections: totalInspectionsCount,
                  approvalRate: approvalRate,
                  avgResponseTime: avgResponseTime,
                  topUser: sortedUsers[0]?.name || 'N/A'
                });
              }
            } catch (e) {
              console.error('Error parsing inspections:', e);
            }
          }
        } else {
          const errorText = response.text || '';
          console.error('❌ Failed to fetch analytics data:', response.status, errorText);
        }
      } catch (error) {
        console.error('❌ Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [backendUrl, dateRange]); // Re-fetch when date range changes

  // Helper: Filter inspections by date range
  const filterInspectionsByDateRange = (inspections) => {
    if (dateRange === 'all') return inspections;

    const now = new Date();
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
    const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

    return inspections.filter(insp => {
      const date = new Date(insp.created_at || insp.capture_date);
      return date >= cutoffDate;
    });
  };

  // Prepare Inspections Over Time chart data
  const getInspectionsOverTimeData = () => {
    if (allInspections.length === 0) return null;

    const filteredInspections = filterInspectionsByDateRange(allInspections);

    const dateGroups = {};
    filteredInspections.forEach(insp => {
      const date = insp.created_at || insp.capture_date;
      if (!date) return;

      const dateStr = new Date(date).toLocaleDateString();
      dateGroups[dateStr] = (dateGroups[dateStr] || 0) + 1;
    });

    const sortedDates = Object.keys(dateGroups).sort((a, b) => new Date(a) - new Date(b)).slice(-14);
    const counts = sortedDates.map(date => dateGroups[date]);

    return {
      labels: sortedDates,
      datasets: [{
        label: 'Inspections',
        data: counts,
        borderColor: '#0DB14B',
        backgroundColor: 'rgba(13, 177, 75, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#0DB14B',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }]
    };
  };

  // Prepare Pye Chart (Status Breakdown) data
  const getPyeChartData = () => {
    if (allInspections.length === 0) return null;

    const filteredInspections = filterInspectionsByDateRange(allInspections);

    const pending = filteredInspections.filter(i =>
      i.status === 'pending' || i.status === 'analyzed' || i.status === 'needs_manual_review'
    ).length;
    const approved = filteredInspections.filter(i => i.status === 'approved').length;
    const rejected = filteredInspections.filter(i => i.status === 'rejected').length;

    return {
      labels: ['Pending', 'Approved', 'Rejected'],
      datasets: [{
        data: [pending, approved, rejected],
        backgroundColor: ['#FFC107', '#0DB14B', '#939598'],
        borderColor: ['#FFF', '#FFF', '#FFF'],
        borderWidth: 2
      }]
    };
  };

  // 🏆 NEW: Prepare Region Competition Bar Chart data (filtered by date range)
  const getRegionBarChartData = () => {
    if (allInspections.length === 0) return null;

    // Filter inspections by date range
    const filteredInspections = filterInspectionsByDateRange(allInspections);

    // Count inspections by region
    const regionCounts = {};
    ALL_REGIONS.forEach(region => regionCounts[region] = 0);

    filteredInspections.forEach(inspection => {
      // Get state from inspection (could be site_state or from address parsing)
      const state = inspection.site_state || inspection.state || '';
      const stateCode = state.toUpperCase().trim();

      // Map state to region
      const region = STATE_TO_REGION[stateCode];
      if (region) {
        regionCounts[region]++;
      } else if (stateCode) {
        // Unknown state - count as 'South' (catch-all)
        regionCounts['South']++;
      }
    });

    // Sort regions by count (descending) for competitive feel
    const sortedRegions = ALL_REGIONS
      .map(region => ({ region, count: regionCounts[region] }))
      .sort((a, b) => b.count - a.count);

    return {
      labels: sortedRegions.map(r => r.region),
      datasets: [{
        label: 'Tag Submissions',
        data: sortedRegions.map(r => r.count),
        backgroundColor: sortedRegions.map(r => REGION_COLORS[r.region]),
        borderColor: sortedRegions.map(r => REGION_COLORS[r.region]),
        borderWidth: 1,
        borderRadius: 6,
        barThickness: 28
      }]
    };
  };

  // Get inspections for a specific region (for drill-down)
  const getInspectionsByRegion = (regionName) => {
    return allInspections.filter(inspection => {
      const state = inspection.site_state || inspection.state || '';
      const stateCode = state.toUpperCase().trim();
      const region = STATE_TO_REGION[stateCode] || 'South';
      return region === regionName;
    });
  };

  // Get inspections by status (for drill-down)
  const getInspectionsByStatus = (status) => {
    if (status === 'Pending') {
      return allInspections.filter(i =>
        i.status === 'pending' || i.status === 'analyzed' || i.status === 'needs_manual_review'
      );
    }
    return allInspections.filter(i => i.status === status.toLowerCase());
  };

  // Get inspections by date (for drill-down)
  const getInspectionsByDate = (dateStr) => {
    return allInspections.filter(inspection => {
      const date = inspection.created_at || inspection.capture_date;
      if (!date) return false;
      return new Date(date).toLocaleDateString() === dateStr;
    });
  };

  // Handle chart click events
  const handleChartClick = (chartType, label, value) => {
    let inspections = [];
    let title = '';

    switch (chartType) {
      case 'region':
        inspections = getInspectionsByRegion(label);
        title = `🏆 ${label} Region - ${value} Submissions`;
        break;
      case 'status':
        inspections = getInspectionsByStatus(label);
        title = `📊 ${label} Inspections - ${value} Total`;
        break;
      case 'timeline':
        inspections = getInspectionsByDate(label);
        title = `📅 ${label} - ${value} Submissions`;
        break;
      default:
        return;
    }

    // Reset status filter when opening modal
    setDrillDownStatusFilter(null);

    setDrillDownModal({
      isOpen: true,
      title,
      type: chartType,
      data: inspections,
      filterValue: label
    });
  };

  // Handle edit submission
  const handleEditInspection = (inspection) => {
    setEditingInspection(inspection);
    setShowEditModal(true);
  };

  const handleSubmissionSaved = (updatedInspection) => {
    // Update the inspection in the drill-down modal data
    if (drillDownModal.data) {
      const updatedData = drillDownModal.data.map(insp =>
        insp.id === updatedInspection.id ? updatedInspection : insp
      );
      setDrillDownModal(prev => ({
        ...prev,
        data: updatedData
      }));
    }
    // Also update in allInspections for chart refresh
    setAllInspections(prev =>
      prev.map(insp => insp.id === updatedInspection.id ? updatedInspection : insp)
    );
    setShowEditModal(false);
    setEditingInspection(null);
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const data = getInspectionsOverTimeData();
        if (data) {
          const label = data.labels[index];
          const value = data.datasets[0].data[index];
          handleChartClick('timeline', label, value);
        }
      }
    },
    plugins: {
      legend: { labels: { color: '#FFF', font: { size: 12 } } },
      tooltip: {
        callbacks: {
          footer: () => '👆 Click to view details'
        }
      }
    },
    scales: {
      y: { ticks: { color: '#939598' }, grid: { color: 'rgba(147, 149, 152, 0.1)' } },
      x: { ticks: { color: '#939598', maxRotation: 45 }, grid: { color: 'rgba(147, 149, 152, 0.1)' } }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const data = getPyeChartData();
        if (data) {
          const label = data.labels[index];
          const value = data.datasets[0].data[index];
          handleChartClick('status', label, value);
        }
      }
    },
    plugins: {
      legend: { position: 'bottom', labels: { color: '#FFF', font: { size: 14 }, padding: 15 } },
      tooltip: {
        callbacks: {
          footer: () => '👆 Click to view details'
        }
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    }
  };

  // 🏆 Bar chart options for Region Competition
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Horizontal bars for better region name visibility
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const data = getRegionBarChartData();
        if (data) {
          const label = data.labels[index];
          const value = data.datasets[0].data[index];
          handleChartClick('region', label, value);
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw} submissions`,
          footer: () => '👆 Click to view region details'
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#FFF', font: { size: 12 } },
        grid: { color: 'rgba(147, 149, 152, 0.1)' },
        title: { display: true, text: 'Tag Submissions', color: '#939598' }
      },
      y: {
        ticks: { color: '#FFF', font: { size: 13, weight: 'bold' } },
        grid: { display: false }
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    }
  };

  const handleExport = async () => {
    try {
      // Fetch all inspections for export
      // SECURITY FIX: Use admin endpoint (has full data for CSV export)
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/inspections?limit=10000&status=all`, {
        headers: { 
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success && data.inspections) {
        // Apply date range filter using the actual date picker values
        let inspectionsToExport = data.inspections;
        
        if (!exportAllData) {
          // Use the date picker values for filtering
          const startDate = new Date(exportStartDate + 'T00:00:00');
          const endDate = new Date(exportEndDate + 'T23:59:59');
          
          inspectionsToExport = data.inspections.filter(insp => {
            const date = new Date(insp.created_at || insp.capture_date || insp.inspection_date);
            return date >= startDate && date <= endDate;
          });
          
          console.log(`📊 Filtered ${data.inspections.length} → ${inspectionsToExport.length} inspections (${exportStartDate} to ${exportEndDate})`);
        } else {
          console.log(`📊 Exporting ALL ${data.inspections.length} inspections`);
        }

        const dateStr = new Date().toISOString().split('T')[0];
        const rangeStr = exportAllData ? 'all' : `${exportStartDate}_to_${exportEndDate}`;

        if (exportFormat === 'json') {
          // JSON export
          const jsonContent = JSON.stringify(inspectionsToExport, null, 2);
          const blob = new Blob([jsonContent], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `inspections-export-${rangeStr}-${dateStr}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          // CSV export (default) - now includes Lead # and Image Link columns
          const csvContent = generateCSV(
            inspectionsToExport,
            (user) => user, // getSubmittedByName
            () => 0, // getUserTotalPoints (not needed for admin export)
            { baseUrl: backendUrl || window.location.origin } // Pass backend URL for image links
          );
          downloadCSV(csvContent, `inspections-export-${rangeStr}-${dateStr}.csv`);
        }
        
        // Show success notification
        console.log(`✅ Exported ${inspectionsToExport.length} inspections as ${exportFormat.toUpperCase()}`);
      } else {
        alert('Failed to fetch inspections for export');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed - check console for details');
    }
  };

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4 w-[calc(100%-1rem)]';
      case 'collapsed': return 'ml-20 w-[calc(100%-5rem)]';
      default: return 'ml-64 w-[calc(100%-16rem)]';
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#00314F] via-[#003d5c] to-[#00314F] text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />
      <div className="pt-6 px-4 max-w-full">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">Analytics & Reports</h1>
            <p className="text-gray-400 text-xs sm:text-sm">System insights and data exports</p>
          </div>

          {/* Date Range Selector */}
          <div className="flex gap-2">
            <button
              onClick={() => setDateRange('7d')}
              className={`px-4 py-2 rounded-lg transition-colors ${dateRange === '7d' ? 'bg-[var(--pbfs-green)]' : 'bg-white/10 border border-white/10 hover:bg-white/20'
                }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDateRange('30d')}
              className={`px-4 py-2 rounded-lg transition-colors ${dateRange === '30d' ? 'bg-[var(--pbfs-green)]' : 'bg-white/10 border border-white/10 hover:bg-white/20'
                }`}
            >
              Last 30 Days
            </button>
            <button
              onClick={() => setDateRange('90d')}
              className={`px-4 py-2 rounded-lg transition-colors ${dateRange === '90d' ? 'bg-[var(--pbfs-green)]' : 'bg-white/10 border border-white/10 hover:bg-white/20'
                }`}
            >
              Last 90 Days
            </button>
            <button
              onClick={() => setDateRange('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${dateRange === 'all' ? 'bg-[var(--pbfs-green)]' : 'bg-white/10 border border-white/10 hover:bg-white/20'
                }`}
            >
              All Time
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
            <p className="text-white/60 mb-2">Total Scans</p>
            <p className="text-4xl font-bold text-[var(--pbfs-blue)]">
              {loading ? '...' : stats.totalInspections.toLocaleString()}
            </p>
            <p className="text-sm text-white/40 mt-2">All time</p>
          </div>

          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
            <p className="text-white/60 mb-2">Tag Lead Approval Rate</p>
            <p className="text-4xl font-bold text-[var(--pbfs-green)]">{stats.approvalRate}%</p>
            <p className="text-sm text-white/40 mt-2">
              {dateRange === 'all' ? 'All time' : 
               dateRange === '7d' ? 'Last 7 days' : 
               dateRange === '30d' ? 'Last 30 days' : 'Last 90 days'}
            </p>
          </div>

          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
            <p className="text-white/60 mb-2">Avg Response Time</p>
            <p className="text-4xl font-bold text-[var(--pbfs-blue)]">{stats.avgResponseTime}</p>
            <p className="text-sm text-white/40 mt-2">From submission to decision</p>
          </div>

          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
            <p className="text-white/60 mb-2">Top Contributor</p>
            <p className="text-xl font-bold text-[var(--pbfs-green)]">
              {loading ? '...' : stats.topUser}
            </p>
            <p className="text-sm text-white/40 mt-2">
              {loading ? '...' : `${topSubmitters[0]?.count || 0} inspections`}
            </p>
          </div>
        </div>

        {/* 🔄 Duplicate Groups Section - Prominent & Easy to Find */}
        <div className="mb-8">
          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-2 border-orange-500/40 rounded-2xl p-6 hover:border-orange-500/60 transition-all shadow-lg hover:shadow-xl">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              {/* Left: Icon & Title */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center text-3xl shadow-lg flex-shrink-0">
                  🔄
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
                    Duplicate Submissions
                    <span className="text-lg bg-orange-500 text-white px-3 py-1 rounded-full font-bold shadow-md">
                      Review Needed
                    </span>
                  </h2>
                  <p className="text-white/70 text-sm mb-3">
                    Multiple submissions detected at the same GPS locations. Review and confirm duplicates to ensure fair scoring.
                  </p>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-orange-400 font-bold text-lg">📍</span>
                      <span className="text-white/60">
                        <span className="text-white font-semibold">~14</span> locations
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-orange-400 font-bold text-lg">📊</span>
                      <span className="text-white/60">
                        <span className="text-white font-semibold">~47</span> potential duplicates
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-orange-400 font-bold text-lg">👥</span>
                      <span className="text-white/60">
                        <span className="text-white font-semibold">Multiple</span> users
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Action Button */}
              <button
                onClick={() => setShowDuplicateListModal(true)}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-bold text-base transition-all shadow-lg hover:shadow-xl flex items-center gap-2 active:scale-95"
              >
                <span>🔍</span>
                <span>Review Duplicates</span>
                <span className="text-white/80">→</span>
              </button>
            </div>

            {/* Bottom: Feature Explanation */}
            <div className="mt-4 pt-4 border-t border-orange-500/30">
              <p className="text-white/50 text-xs flex items-center gap-2">
                <span>💡</span>
                <span>
                  <strong className="text-white/70">How it works:</strong> Scroll to the map below, zoom in to see clustered pins, 
                  click any cluster to review submissions at that location, then confirm duplicates to adjust scores fairly.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Inspections Over Time Chart - CLICKABLE */}
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 hover:border-[var(--pbfs-green)]/30 transition-colors">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Inspections Over Time</h2>
              <span className="text-xs text-white/40 bg-white/10 px-2 py-1 rounded">👆 Click bars for details</span>
            </div>
            <div className="h-64">
              {getInspectionsOverTimeData() ? (
                <Line data={getInspectionsOverTimeData()} options={lineChartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-white/40">No inspection data yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Pye Chart (Status Breakdown) - CLICKABLE */}
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 hover:border-[var(--pbfs-green)]/30 transition-colors">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold">Pye Chart</h2>
                <p className="text-white/60 text-sm">Pending vs Approved vs Rejected</p>
              </div>
              <span className="text-xs text-white/40 bg-white/10 px-2 py-1 rounded">👆 Click slices for details</span>
            </div>
            <div className="h-64">
              {getPyeChartData() ? (
                <Pie data={getPyeChartData()} options={pieChartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-white/40">No inspection data yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 🏆 Region Competition Section - Sales Leaders will love this! */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8 hover:border-[var(--pbfs-green)]/30 transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                🏆 Regional Competition
              </h2>
              <p className="text-white/60 text-sm mt-1">Tag submissions by Pye-Barker region • Who's leading the pack?</p>
            </div>
            <span className="text-xs text-white/40 bg-white/10 px-2 py-1 rounded">👆 Click bars for details</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Bar Chart - Takes 2/3 of the space */}
            <div className="lg:col-span-2 h-80">
              {getRegionBarChartData() ? (
                <Bar data={getRegionBarChartData()} options={barChartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-white/40">No regional data yet</p>
                </div>
              )}
            </div>

            {/* Regional Leaderboard - Quick Stats */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white/80 mb-4">🥇 Regional Rankings</h3>
              {getRegionBarChartData() ? (
                getRegionBarChartData().labels.slice(0, 5).map((region, index) => {
                  const count = getRegionBarChartData().datasets[0].data[index];
                  const isLeader = index === 0;
                  const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`;

                  return (
                    <button
                      key={region}
                      onClick={() => handleChartClick('region', region, count)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all hover:scale-[1.02] ${isLeader
                        ? 'bg-gradient-to-r from-[var(--pbfs-green)]/20 to-transparent border border-[var(--pbfs-green)]/40'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{medal}</span>
                        <span className={`font-semibold ${isLeader ? 'text-[var(--pbfs-green)]' : 'text-white'}`}>
                          {region}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className={`text-xl font-bold ${isLeader ? 'text-[var(--pbfs-green)]' : 'text-white'}`}>
                          {count}
                        </span>
                        <span className="text-white/40 text-xs ml-1">tags</span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="text-white/40 text-center py-4">No data</p>
              )}

              {/* View All Regions Link */}
              {getRegionBarChartData() && getRegionBarChartData().labels.length > 5 && (
                <button
                  onClick={() => {
                    const chartData = getRegionBarChartData();
                    const regionRankings = chartData.labels.map((region, idx) => ({
                      region,
                      count: chartData.datasets[0].data[idx],
                      rank: idx + 1
                    }));
                    setDrillDownModal({
                      isOpen: true,
                      title: '🏆 All Regional Rankings',
                      type: 'all-regions',
                      data: regionRankings, // Pass region rankings, not inspections
                      filterValue: null
                    });
                  }}
                  className="w-full text-center text-[var(--pbfs-blue)] hover:text-[var(--pbfs-green)] text-sm py-2 transition-colors"
                >
                  View all {getRegionBarChartData().labels.length} regions →
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 🗺️ Analytics Map (standalone component to keep this page clean) */}
        <div id="analytics-map-section" className="mb-8 scroll-mt-20">
          <AnalyticsMap backendUrl={backendUrl} dateRange={dateRange} />
        </div>

        {/* Leaderboard */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Leaderboard</h2>
          {loading ? (
            <div className="text-center text-gray-400 py-8">
              <p>Loading leaderboard...</p>
            </div>
          ) : topSubmitters.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <p>No users yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topSubmitters.map((user) => (
                <div key={user.rank} className="flex items-center justify-between bg-white/5 border border-white/10 p-4 rounded-lg hover:bg-white/10 transition-all">
                  <div className="flex items-center gap-4">
                    <span className={`text-2xl font-bold ${user.rank === 1 ? 'text-[var(--pbfs-green)]' :
                      user.rank === 2 ? 'text-[var(--pbfs-blue)]' :
                        user.rank === 3 ? 'text-[var(--pbfs-blue)]' :
                          'text-white/60'
                      }`}>
                      #{user.rank}
                    </span>
                    <div>
                      <button
                        onClick={() => onViewUserSubmissions && onViewUserSubmissions(user.name)}
                        className="font-semibold text-white hover:text-[var(--pbfs-green)] hover:underline transition-colors cursor-pointer text-left"
                        title="View submissions"
                      >
                        {user.name}
                      </button>
                      <p className="text-sm text-white/50">{user.count} inspections</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[var(--pbfs-green)]">{user.points.toLocaleString()}</p>
                    <p className="text-xs text-white/50">points</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Export Section - Responsive Layout */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4">💾 Export Data</h2>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-auto">
              <label className="block text-gray-400 mb-2">Format</label>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                className="w-full md:w-40 bg-[#00314F] text-white border border-white/20 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500"
                style={{ colorScheme: 'dark' }}
              >
                <option value="csv" className="bg-[#00314F] text-white">CSV</option>
                <option value="json" className="bg-[#00314F] text-white">JSON</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-gray-400 mb-2">Date Range</label>
              <div className="flex flex-col gap-4">
                {/* Date Inputs - Stack on small, row on generic mobile */}
                <div className="flex flex-col sm:flex-row gap-2 items-center">
                  <input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    disabled={exportAllData}
                    className={`w-full sm:w-auto flex-1 bg-gray-900 text-white border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 ${exportAllData ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ colorScheme: 'dark' }}
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    disabled={exportAllData}
                    className={`w-full sm:w-auto flex-1 bg-gray-900 text-white border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 ${exportAllData ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                {/* Quick Presets & Checkbox */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        const today = new Date();
                        const last7 = new Date(today);
                        last7.setDate(today.getDate() - 7);
                        setExportStartDate(last7.toISOString().split('T')[0]);
                        setExportEndDate(today.toISOString().split('T')[0]);
                        setExportAllData(false);
                        setDateRange('7d');
                      }}
                      className={`px-3 py-1 rounded text-xs transition-colors ${dateRange === '7d' && !exportAllData ? 'bg-[var(--pbfs-green)] text-white' : 'bg-white/10 hover:bg-white/20 text-white/80'}`}
                    >
                      Last 7 Days
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date();
                        const last30 = new Date(today);
                        last30.setDate(today.getDate() - 30);
                        setExportStartDate(last30.toISOString().split('T')[0]);
                        setExportEndDate(today.toISOString().split('T')[0]);
                        setExportAllData(false);
                        setDateRange('30d');
                      }}
                      className={`px-3 py-1 rounded text-xs transition-colors ${dateRange === '30d' && !exportAllData ? 'bg-[var(--pbfs-green)] text-white' : 'bg-white/10 hover:bg-white/20 text-white/80'}`}
                    >
                      30 Days
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date();
                        const last90 = new Date(today);
                        last90.setDate(today.getDate() - 90);
                        setExportStartDate(last90.toISOString().split('T')[0]);
                        setExportEndDate(today.toISOString().split('T')[0]);
                        setExportAllData(false);
                        setDateRange('90d');
                      }}
                      className={`px-3 py-1 rounded text-xs transition-colors ${dateRange === '90d' && !exportAllData ? 'bg-[var(--pbfs-green)] text-white' : 'bg-white/10 hover:bg-white/20 text-white/80'}`}
                    >
                      90 Days
                    </button>
                  </div>

                  <label className="flex items-center gap-2 text-white/80 hover:text-white cursor-pointer ml-auto sm:ml-0">
                    <input
                      type="checkbox"
                      checked={exportAllData}
                      onChange={(e) => setExportAllData(e.target.checked)}
                      className="w-4 h-4 accent-[var(--pbfs-green)] cursor-pointer"
                    />
                    <span className="text-sm border-b border-dashed border-white/40">Export All</span>
                  </label>
                </div>

              </div>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleExport}
                className="w-full md:w-auto bg-green-600 hover:bg-green-500 px-6 py-2.5 rounded-lg font-bold text-white shadow-lg transition-colors flex items-center justify-center gap-2"
              >
                <span>📥</span> Export File
              </button>
            </div>
          </div>
        </div>

        {/* Updated Notice */}
        <div className="mt-6 bg-[var(--pbfs-green)]/20 border border-[var(--pbfs-green)] rounded-lg p-4 text-center">
          <p className="text-[var(--pbfs-green)] font-semibold">
            ✅ Interactive Analytics Now Live!
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Click on any chart element to drill down into the data. Regional competition tracking is active!
          </p>
        </div>
      </div>

      {/* 📊 Drill-Down Modal - Shows detailed inspection data when clicking charts */}
      {drillDownModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-[var(--pbfs-navy)] border border-white/20 rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-2xl font-bold text-white">{drillDownModal.title}</h2>
                <p className="text-white/60 text-sm mt-1">
                  {drillDownModal.data?.length || 0} inspection{drillDownModal.data?.length !== 1 ? 's' : ''} found
                </p>
              </div>
              <button
                onClick={() => setDrillDownModal({ isOpen: false, title: '', type: '', data: null, filterValue: null })}
                className="text-white/60 hover:text-white text-3xl transition-colors"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {drillDownModal.data && drillDownModal.data.length > 0 ? (
                <div className="space-y-3">
                  {/* Summary Stats for Region drill-down - CLICKABLE to filter */}
                  {drillDownModal.type === 'region' && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <button
                        onClick={() => setDrillDownStatusFilter(drillDownStatusFilter === 'approved' ? null : 'approved')}
                        className={`rounded-lg p-4 text-center transition-all transform hover:scale-105 cursor-pointer ${drillDownStatusFilter === 'approved'
                          ? 'bg-[var(--pbfs-green)]/30 border-2 border-[var(--pbfs-green)] ring-2 ring-[var(--pbfs-green)]/50'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                          }`}
                      >
                        <p className="text-3xl font-bold text-[var(--pbfs-green)]">
                          {drillDownModal.data.filter(i => i.status === 'approved').length}
                        </p>
                        <p className="text-white/60 text-sm">Approved</p>
                        {drillDownStatusFilter === 'approved' && (
                          <p className="text-[var(--pbfs-green)] text-xs mt-1">✓ Filtered</p>
                        )}
                      </button>
                      <button
                        onClick={() => setDrillDownStatusFilter(drillDownStatusFilter === 'pending' ? null : 'pending')}
                        className={`rounded-lg p-4 text-center transition-all transform hover:scale-105 cursor-pointer ${drillDownStatusFilter === 'pending'
                          ? 'bg-yellow-400/30 border-2 border-yellow-400 ring-2 ring-yellow-400/50'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                          }`}
                      >
                        <p className="text-3xl font-bold text-yellow-400">
                          {drillDownModal.data.filter(i => i.status === 'pending' || i.status === 'analyzed').length}
                        </p>
                        <p className="text-white/60 text-sm">Pending</p>
                        {drillDownStatusFilter === 'pending' && (
                          <p className="text-yellow-400 text-xs mt-1">✓ Filtered</p>
                        )}
                      </button>
                      <button
                        onClick={() => setDrillDownStatusFilter(drillDownStatusFilter === 'rejected' ? null : 'rejected')}
                        className={`rounded-lg p-4 text-center transition-all transform hover:scale-105 cursor-pointer ${drillDownStatusFilter === 'rejected'
                          ? 'bg-gray-500/30 border-2 border-gray-400 ring-2 ring-gray-400/50'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                          }`}
                      >
                        <p className="text-3xl font-bold text-white/60">
                          {drillDownModal.data.filter(i => i.status === 'rejected').length}
                        </p>
                        <p className="text-white/60 text-sm">Rejected</p>
                        {drillDownStatusFilter === 'rejected' && (
                          <p className="text-gray-400 text-xs mt-1">✓ Filtered</p>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Show Regional Rankings or Inspection List */}
                  <div className="space-y-2">
                    {drillDownModal.type === 'all-regions' ? (
                      /* Regional Rankings View - CLICKABLE to open region details */
                      drillDownModal.data.map((ranking, index) => {
                        const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`;
                        const isLeader = index === 0;

                        return (
                          <button
                            key={ranking.region}
                            onClick={() => handleChartClick('region', ranking.region, ranking.count)}
                            className={`w-full flex items-center justify-between p-4 rounded-lg transition-all hover:scale-[1.01] cursor-pointer ${isLeader
                              ? 'bg-gradient-to-r from-[var(--pbfs-green)]/20 to-transparent border-2 border-[var(--pbfs-green)] hover:from-[var(--pbfs-green)]/30'
                              : 'bg-white/5 border border-white/10 hover:bg-white/10'
                              }`}
                          >
                            <div className="flex items-center gap-4">
                              <span className="text-2xl">{medal}</span>
                              <span className={`text-lg font-semibold ${isLeader ? 'text-[var(--pbfs-green)]' : 'text-white'}`}>
                                {ranking.region}
                              </span>
                            </div>
                            <div className="text-right flex items-center gap-3">
                              <div>
                                <span className={`text-2xl font-bold ${isLeader ? 'text-[var(--pbfs-green)]' : 'text-white'}`}>
                                  {ranking.count}
                                </span>
                                <span className="text-white/40 text-sm ml-2">tags</span>
                              </div>
                              <span className="text-white/40 text-xl">›</span>
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      /* Inspection List View - Filtered by status if drillDownStatusFilter is set */
                      (() => {
                        // Apply status filter if set
                        let filteredData = drillDownModal.data;
                        if (drillDownStatusFilter === 'approved') {
                          filteredData = drillDownModal.data.filter(i => i.status === 'approved');
                        } else if (drillDownStatusFilter === 'pending') {
                          filteredData = drillDownModal.data.filter(i => i.status === 'pending' || i.status === 'analyzed');
                        } else if (drillDownStatusFilter === 'rejected') {
                          filteredData = drillDownModal.data.filter(i => i.status === 'rejected');
                        }

                        return filteredData.slice(0, 50).map((inspection, index) => {
                          const state = inspection.site_state || inspection.state || 'N/A';
                          const city = inspection.site_city || inspection.city || '';
                          const businessName = inspection.business_name || 'Unknown Business';
                          const submittedBy = inspection.submitted_by || 'Anonymous';
                          const submittedByEmail = inspection.submitted_by_email || '';
                          const date = inspection.created_at ? new Date(inspection.created_at).toLocaleDateString() : 'N/A';
                          const status = inspection.status || 'pending';

                          // 🆕 Hierarchy info (RSL/ASM)
                          const rslName = inspection.regional_sales_leader_name || '';
                          const asmName = inspection.area_sales_manager_name || '';

                          const statusColors = {
                            'approved': 'bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] border-[var(--pbfs-green)]/40',
                            'pending': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
                            'analyzed': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
                            'rejected': 'bg-gray-500/20 text-gray-400 border-gray-500/40',
                            'needs_manual_review': 'bg-orange-500/20 text-orange-400 border-orange-500/40'
                          };

                          return (
                            <div
                              key={inspection.id || index}
                              className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 hover:border-white/20 transition-all"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  {/* Business name + status badge */}
                                  <div className="flex items-center gap-3 mb-2">
                                    <h4 className="text-white font-semibold truncate">{businessName}</h4>
                                    <span className={`text-xs px-2.5 py-1 rounded-full border font-medium shrink-0 ${statusColors[status] || statusColors['pending']}`}>
                                      {status.replace('_', ' ')}
                                    </span>
                                  </div>
                                  {/* Location & submitter info */}
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/60">
                                    <span className="flex items-center gap-1">
                                      <span className="text-white/40">📍</span>
                                      {city}{city && state ? ', ' : ''}{state}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <span className="text-white/40">👤</span>
                                      {submittedBy}
                                      {submittedByEmail && <span className="text-white/40">({submittedByEmail})</span>}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <span className="text-white/40">📅</span>
                                      {date}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleEditInspection(inspection)}
                                  className="shrink-0 bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-green)] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
                                >
                                  <span>✏️</span> View
                                </button>
                              </div>
                            </div>
                          );
                        });
                      })()
                    )}

                    {drillDownModal.type !== 'all-regions' && (() => {
                      // Calculate filtered count
                      let filteredCount = drillDownModal.data.length;
                      if (drillDownStatusFilter === 'approved') {
                        filteredCount = drillDownModal.data.filter(i => i.status === 'approved').length;
                      } else if (drillDownStatusFilter === 'pending') {
                        filteredCount = drillDownModal.data.filter(i => i.status === 'pending' || i.status === 'analyzed').length;
                      } else if (drillDownStatusFilter === 'rejected') {
                        filteredCount = drillDownModal.data.filter(i => i.status === 'rejected').length;
                      }

                      return filteredCount > 50 && (
                        <div className="text-center text-white/40 py-4">
                          Showing first 50 of {filteredCount} {drillDownStatusFilter ? `${drillDownStatusFilter} ` : ''}inspections
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="text-center text-white/40 py-12">
                  <p className="text-4xl mb-4">📭</p>
                  <p>No inspections found for this filter</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 border-t border-white/10 bg-gradient-to-r from-white/5 to-transparent">
              <div className="text-white/60 text-sm">
                {drillDownModal.type === 'region' && (
                  <span className="flex items-center gap-2">
                    <span className="text-xl">🏆</span>
                    Region: <strong className="text-white ml-1">{drillDownModal.filterValue}</strong>
                  </span>
                )}
                {drillDownModal.type === 'status' && (
                  <span className="flex items-center gap-2">
                    <span className="text-xl">📊</span>
                    Status: <strong className="text-white ml-1">{drillDownModal.filterValue}</strong>
                  </span>
                )}
                {drillDownModal.type === 'timeline' && (
                  <span className="flex items-center gap-2">
                    <span className="text-xl">📅</span>
                    Date: <strong className="text-white ml-1">{drillDownModal.filterValue}</strong>
                  </span>
                )}
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    // Navigate to Tag Leads with filter applied
                    if (drillDownModal.type === 'region') {
                      navigate('/admin/approvals', { state: { filterRegion: drillDownModal.filterValue } });
                    } else if (drillDownModal.type === 'status') {
                      navigate('/admin/approvals', { state: { filterStatus: drillDownModal.filterValue.toLowerCase() } });
                    } else {
                      navigate('/admin/approvals');
                    }
                    setDrillDownModal({ isOpen: false, title: '', type: '', data: null, filterValue: null });
                  }}
                  className="flex-1 sm:flex-none bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 text-white px-6 py-2.5 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  View in Tag Leads <span>→</span>
                </button>
                <button
                  onClick={() => setDrillDownModal({ isOpen: false, title: '', type: '', data: null, filterValue: null })}
                  className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✏️ Edit Submission Modal - Opens when clicking View button on inspections */}
      <EditSubmissionModal
        isVisible={showEditModal}
        inspection={editingInspection}
        currentUser={editingInspection?.submitted_by_email || ''}
        onClose={() => {
          setShowEditModal(false);
          setEditingInspection(null);
        }}
        onSaved={handleSubmissionSaved}
      />

      {/* 🔍 Duplicate List Modal - Opens from "Review Duplicates" button */}
      <DuplicateListModal
        isOpen={showDuplicateListModal}
        onClose={() => setShowDuplicateListModal(false)}
        backendUrl={backendUrl}
      />
    </div>
  );
};

export default AnalyticsReports;


